import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { LogoWordmark } from "./Logo";
import { SweepLink } from "./Reveal";

const ADVISORY = [
  { name: "Strategic Advisory & M&A", href: "/strategic-advisory" },
  { name: "Private Capital", href: "/private-capital" },
  { name: "Family Office Advisory", href: "/family-office-advisory" },
  { name: "Sports Investments", href: "/sports-investments" },
];

const LINKS = [
  { name: "About PJG", href: "/about", testid: "nav-link-about" },
  { name: "Sectors", href: "/sectors", testid: "nav-link-sectors" },
  { name: "Insights", href: "/insights", testid: "nav-link-insights" },
  { name: "Contact", href: "/contact", testid: "nav-link-contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 1.6 }}
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 border-b ${
        scrolled
          ? "bg-[#F6F4F0]/90 backdrop-blur-2xl border-[#E3E0D8] shadow-[0_8px_30px_rgba(31,30,27,0.06)]"
          : "bg-[#F6F4F0]/40 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" aria-label="PJG Capital Partners — Home" data-testid="nav-link-home">
          <LogoWordmark />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          <div className="relative group">
            <button
              data-testid="nav-advisory-dropdown"
              className="flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.22em] text-[#1F1E1B]/75 hover:text-[#1F1E1B] transition-colors duration-300 py-2"
            >
              Expertise
              <ChevronDown size={12} className="transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-[opacity,transform,visibility] duration-300">
              <div className="w-72 bg-[#FFFFFF] border border-[#E3E0D8] shadow-[0_20px_50px_rgba(31,30,27,0.1)] py-2">
                {ADVISORY.map((a) => (
                  <Link
                    key={a.href}
                    to={a.href}
                    data-testid={`nav-dropdown-${a.href.slice(1)}`}
                    className="block px-6 py-3 text-sm text-[#1F1E1B]/80 hover:text-[#1F1E1B] hover:bg-[#F6F4F0] transition-colors duration-200"
                  >
                    {a.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {LINKS.map((l) => (
            <Link
              key={l.name}
              to={l.href}
              data-testid={l.testid}
              className="group relative font-sans text-[11px] uppercase tracking-[0.22em] text-[#1F1E1B]/75 hover:text-[#1F1E1B] transition-colors duration-300 py-2"
            >
              {l.name}
              <span className="absolute bottom-0 left-0 h-px w-0 bg-[#1F1E1B] transition-[width] duration-400 ease-out group-hover:w-full" />
            </Link>
          ))}

          <SweepLink href="/contact" testid="nav-cta-discuss-button" small>
            Discuss a Transaction
          </SweepLink>
        </nav>

        <button
          className="lg:hidden text-[#1F1E1B] p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-menu-button"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#F6F4F0]/97 backdrop-blur-2xl border-t border-[#E3E0D8]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              <p className="font-sans text-[9px] uppercase tracking-[0.3em] text-[#6E6A61]">Expertise</p>
              {ADVISORY.map((a) => (
                <Link
                  key={a.href}
                  to={a.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${a.href.slice(1)}`}
                  className="font-serif text-lg text-[#1F1E1B] pl-4"
                >
                  {a.name}
                </Link>
              ))}
              <div className="h-px bg-[#E3E0D8] my-1" />
              {LINKS.map((l) => (
                <Link
                  key={l.name}
                  to={l.href}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => setOpen(false)}
                  className="font-sans text-xs uppercase tracking-[0.24em] text-[#1F1E1B]/80 hover:text-[#1F1E1B] transition-colors"
                >
                  {l.name}
                </Link>
              ))}
              <div onClick={() => setOpen(false)}>
                <SweepLink href="/contact" testid="mobile-nav-cta-discuss-button" small>
                  Discuss a Transaction
                </SweepLink>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
